import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClassStudents } from '../../../redux/sclassRelated/sclassHandle';
import { updateStudentField } from '../../../redux/studentRelated/studentHandle';
import {
    Paper, Box, Typography, Button, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, TextField
} from '@mui/material';

const StudentExamMarks = () => {
    const dispatch = useDispatch();
    const { sclassStudents, loading, error } = useSelector(state => state.sclass);
    const { response, statestatus, error: submitError } = useSelector(state => state.student);
    const { currentUser } = useSelector(state => state.user);

    const classID = currentUser?.teachSclass?._id;
    const subjectID = currentUser?.teachSubject?._id;

    const [marksData, setMarksData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState("");

    useEffect(() => {
        if (classID) {
            dispatch(getClassStudents(classID));
        }
    }, [dispatch, classID]);

    const handleMarksChange = (studentId, marks) => {
        setMarksData(prev => ({ ...prev, [studentId]: marks }));
    };

    const handleSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        setMessage("");

        const marksArray = Object.keys(marksData).map(studentId => ({
            studentId,
            subName: subjectID,
            marksObtained: Number(marksData[studentId]),
            date: new Date().toISOString()
        }));

        if (marksArray.length === 0) {
            setMessage("⚠ Please enter marks for at least one student.");
            setIsSubmitting(false);
            return;
        }

        try {
            const result = await dispatch(updateStudentField(classID, marksArray, "UpdateExamResult"));
            if (result?.success) {
                setMessage("✅ Marks updated successfully!");
                setMarksData({});
            } else {
                const errorMsg = result?.error?.message || result?.error || "❌ Failed to update marks";
                setMessage(errorMsg);
            }
        } catch (error) {
            setMessage("❌ An unexpected error occurred: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        let responseMessage = response?.message || (statestatus === "added" ? "Marks submitted successfully" : "");
        setMessage(responseMessage || "");

        if (submitError) {
            setMessage(submitError.message || "Error submitting marks");
        }
    }, [response, statestatus, submitError]);

    return (
        <Box sx={{ padding: { xs: 1, sm: 3 }, backgroundColor: '#E0F2F1', minHeight: '100vh' }}>
            <Typography variant="h4" gutterBottom sx={{ 
                textAlign: 'center', 
                mb: 4, 
                color: '#00695C', 
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', sm: '2rem' } 
            }}>
                🏆 Student Exam Marks Management
            </Typography>

            {loading ? (
                <Box textAlign="center"><CircularProgress /></Box>
            ) : error ? (
                <Typography color="error" textAlign="center">❌ Error: {error}</Typography>
            ) : sclassStudents.length === 0 ? (
                <Typography textAlign="center">⚠ No students found in this class</Typography>
            ) : (
                <Paper elevation={5} sx={{ 
                    p: { xs: 1, sm: 3 }, 
                    maxWidth: { xs: '100%', sm: 800 }, 
                    mx: 'auto', 
                    borderRadius: 4, 
                    boxShadow: 5 
                }}>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#00897B' }}>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Student Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', color: 'white', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Roll Number</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', color: 'white', fontSize: { xs: '0.875rem', sm: '1rem' } }}>Marks</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sclassStudents.map(student => (
                                    <TableRow key={student._id} sx={{ '&:hover': { bgcolor: '#FBE9E7' } }}>
                                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{student.name}</TableCell>
                                        <TableCell sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>{student.rollNum}</TableCell>
                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                value={marksData[student._id] || ""}
                                                onChange={(e) => handleMarksChange(student._id, e.target.value)}
                                                variant="outlined"
                                                size="small"
                                                sx={{ 
                                                    width: { xs: 80, sm: 100 }, 
                                                    bgcolor: '#FFF3E0', 
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-input': { fontSize: { xs: '0.875rem', sm: '1rem' } }
                                                }}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>

                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{
                                bgcolor: '#FFD700',
                                color: '#00695C',
                                fontWeight: 'bold',
                                '&:hover': { bgcolor: '#FFC107' },
                                borderRadius: 3,
                                padding: { xs: '8px 16px', sm: '10px 20px' },
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}
                        >
                            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : '✅ Submit Marks'}
                        </Button>

                        {message && (
                            <Typography sx={{ 
                                mt: 2, 
                                fontWeight: 'bold',
                                color: message.includes("✅") ? 'success.main' : 'error.main',
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                            }}>
                                {message}
                            </Typography>
                        )}
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default StudentExamMarks;